import React, {
  createContext, useState, useLayoutEffect, useMemo, useContext,
} from 'react';
import PropTypes from 'prop-types';

import strings, { LANGUAGES } from '../data/i18n';

const LanguageContext = createContext({
  lang: 'en',
  t: strings.en,
  setLang: () => { },
  toggleLang: () => { },
});

const readSavedLang = () => {
  // A language prefix in the URL (/pl, /en/about) wins over everything else
  const fromUrl = window.location.pathname.match(/^\/(en|pl)(\/|$)/);
  if (fromUrl) return fromUrl[1];
  try {
    const saved = window.localStorage.getItem('lang');
    if (LANGUAGES.includes(saved)) return saved;
  } catch (e) {
    // localStorage may be unavailable (private mode); fall through
  }
  return (navigator.language || '').toLowerCase().startsWith('pl') ? 'pl' : 'en';
};

export const LanguageProvider = ({ children }) => {
  // Initial state must be deterministic for hydration (React 18)
  const [lang, setLangState] = useState('en');

  useLayoutEffect(() => {
    const initial = readSavedLang();
    if (initial !== lang) setLangState(initial);
  }, []);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setLang = (next) => {
    if (!LANGUAGES.includes(next)) return;
    try {
      window.localStorage.setItem('lang', next);
    } catch (e) {
      // ignore
    }
    setLangState(next);
  };

  const value = useMemo(() => ({
    lang,
    t: strings[lang],
    setLang,
    toggleLang: () => setLang(lang === 'en' ? 'pl' : 'en'),
  }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => useContext(LanguageContext);

export default LanguageContext;
